import { ListItemIcon, ListItemText, Menu, MenuItem, Stack, Typography } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core"
import { faBolt, faGear } from "@fortawesome/pro-light-svg-icons"
import { faSparkles } from "@fortawesome/pro-solid-svg-icons"
import { makeStyles } from "tss-react/mui"

import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import type { TranslationKey } from "../../../../../../localization/translations"
import { StandardButton } from "../../../../../../../shared/components/buttons/StandardButton"
import { useAnchorMenu } from "../../../../../../../shared/hooks/useAnchorMenu"

const useStyles = makeStyles()((theme) => ({
    button: {
        "&.MuiButton-root": {
            height: "2.75rem",
        },
        "& .MuiButton-startIcon svg": {
            fontSize: "1rem",
        },
    },
    menuItem: {
        "&.MuiMenuItem-root": {
            padding: ".75rem",
            alignItems: "flex-start",
            whiteSpace: "normal",
        },
    },
    paper: {
        width: "22rem",
        marginTop: ".5rem",
        "& .MuiList-root": {
            padding: "1rem",
        },
    },
    title: {
        fontWeight: 600,
    },
    subtitle: {
        color: theme.palette.text.secondary,
    },
    itemIcon: {
        "&&": {
            minWidth: "auto",
            marginRight: ".75rem",
        },
    },
    iconBox: {
        width: "2.5rem",
        height: "2.5rem",
        borderRadius: ".375rem",
        "& svg": {
            width: "1rem",
            height: "1rem",
        },
    },
    iconBoxQuick: {
        background: theme.palette.tailwind.green[50],
        border: `1px solid ${theme.palette.tailwind.green[300]}`,
        color: theme.palette.tailwind.green[600],
    },
    iconBoxCustomize: {
        background: theme.palette.tailwind.blue[50],
        border: `1px solid ${theme.palette.tailwind.blue[300]}`,
        color: theme.palette.tailwind.blue[600],
    },
}))

type AiMenuEntry = Readonly<{
    titleId: TranslationKey
    title: string
    subtitleId: TranslationKey
    subtitle: string
    icon: IconDefinition
    iconBoxClass: string
    onClick: () => void
}>

type Props = Readonly<{
    onQuickGenerate: () => void
    onCustomize: () => void
}>

export function AiMenuButton({ onQuickGenerate, onCustomize }: Props) {
    const { classes, cx } = useStyles()
    const { anchorEl, isOpen, open, close, closeThen } = useAnchorMenu()

    const entries: ReadonlyArray<AiMenuEntry> = [
        {
            titleId: "merchant-week-menu-ai-menu-quick-title",
            title: "Quick Menu",
            subtitleId: "merchant-week-menu-ai-menu-quick-subtitle",
            subtitle: "Generate the whole week automatically",
            icon: faBolt,
            iconBoxClass: classes.iconBoxQuick,
            onClick: onQuickGenerate,
        },
        {
            titleId: "merchant-week-menu-ai-menu-customize-title",
            title: "Customize Menu",
            subtitleId: "merchant-week-menu-ai-menu-customize-subtitle",
            subtitle: "Set themes & rules per day, then generate",
            icon: faGear,
            iconBoxClass: classes.iconBoxCustomize,
            onClick: onCustomize,
        },
    ]

    return (
        <>
            <LocalizedStrict id="merchant-week-menu-ai-button">
                <StandardButton
                    fullWidth={false}
                    color="purple"
                    className={classes.button}
                    startIcon={<FontAwesomeIcon icon={faSparkles} />}
                    onClick={open}
                >
                    AI
                </StandardButton>
            </LocalizedStrict>
            <Menu
                open={isOpen}
                anchorEl={anchorEl}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                slotProps={{ paper: { className: classes.paper } }}
                onClose={close}
            >
                {entries.map((entry) => (
                    <MenuItem key={entry.titleId} className={classes.menuItem} onClick={closeThen(entry.onClick)}>
                        <ListItemIcon className={classes.itemIcon}>
                            <Stack
                                alignItems="center"
                                justifyContent="center"
                                className={cx(classes.iconBox, entry.iconBoxClass)}
                            >
                                <FontAwesomeIcon icon={entry.icon} />
                            </Stack>
                        </ListItemIcon>
                        <ListItemText
                            disableTypography
                            primary={
                                <LocalizedStrict id={entry.titleId}>
                                    <Typography variant="subtitle1" className={classes.title}>
                                        {entry.title}
                                    </Typography>
                                </LocalizedStrict>
                            }
                            secondary={
                                <LocalizedStrict id={entry.subtitleId}>
                                    <Typography variant="body2" className={classes.subtitle}>
                                        {entry.subtitle}
                                    </Typography>
                                </LocalizedStrict>
                            }
                        />
                    </MenuItem>
                ))}
            </Menu>
        </>
    )
}
